import { Group, PerspectiveCamera, Scene } from "three";
import {
	CSS3DObject,
	CSS3DRenderer,
} from "three/examples/jsm/renderers/CSS3DRenderer.js";

export function renderCylinder(items, renderItem) {
	const wrapper = document.createElement("div");
	wrapper.className = "sui-topic-cloud-cylinder";

	const scene = document.createElement("div");
	scene.className = "sui-topic-cloud-scene sui-topic-cloud-scene--cylinder";

	items.forEach((item, index) => {
		scene.appendChild(renderItem(item, index, items.length, `${index}`));
	});

	const controls = document.createElement("div");
	controls.className = "sui-topic-cloud-cylinder-controls";

	const previousButton = document.createElement("button");
	previousButton.type = "button";
	previousButton.className = "sui-topic-cloud-cylinder-button";
	previousButton.dataset.cylinderAction = "previous";
	previousButton.setAttribute("aria-label", "Rotate cylinder left");
	const previousIcon = document.createElement("span");
	previousIcon.setAttribute("aria-hidden", "true");
	previousIcon.innerHTML = "&larr;";
	previousButton.appendChild(previousIcon);

	const nextButton = document.createElement("button");
	nextButton.type = "button";
	nextButton.className = "sui-topic-cloud-cylinder-button";
	nextButton.dataset.cylinderAction = "next";
	nextButton.setAttribute("aria-label", "Rotate cylinder right");
	const nextIcon = document.createElement("span");
	nextIcon.setAttribute("aria-hidden", "true");
	nextIcon.innerHTML = "&rarr;";
	nextButton.appendChild(nextIcon);

	controls.append(previousButton, nextButton);
	wrapper.append(scene, controls);

	return wrapper;
}

export function initializeCylinder(node, { clamp, getCountWeight }) {
	const sceneNode = node.querySelector(".sui-topic-cloud-scene--cylinder");
	const previousButton = node.querySelector(
		'[data-cylinder-action="previous"]',
	);
	const nextButton = node.querySelector('[data-cylinder-action="next"]');
	const items = Array.from(
		sceneNode?.querySelectorAll(".sui-topic-cloud-item") || [],
	);
	const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

	if (!sceneNode || !items.length || motionQuery.matches) {
		return;
	}

	node.classList.add("is-three-active");

	const renderer = new CSS3DRenderer();
	renderer.domElement.className = "sui-topic-cloud-cylinder-renderer";

	const scene = new Scene();
	const camera = new PerspectiveCamera(34, 1, 1, 4000);
	const cylinderGroup = new Group();
	scene.add(cylinderGroup);
	const gapX = 14;
	const gapY = 8;
	const itemMetrics = items.map((item) => {
		const rect = item.getBoundingClientRect();

		return {
			height: Math.max(rect.height || 0, 48),
			width: Math.max(rect.width || 0, 120),
		};
	});
	const cylinderObjects = [];

	items.forEach((item, index) => {
		const object = new CSS3DObject(item);
		item.style.backfaceVisibility = "visible";
		cylinderObjects.push({
			item,
			metric: itemMetrics[index],
			object,
		});
		cylinderGroup.add(object);
	});

	sceneNode.replaceChildren(renderer.domElement);

	let currentRotation = 0;
	let targetRotation = 0;
	let frameId = 0;
	let rotationStep = Math.PI / 6;
	const sensitivity = Number(node.dataset.scrollSensitivity || 0.16);

	const renderScene = () => {
		renderer.render(scene, camera);
	};

	const updateSize = () => {
		const width = sceneNode.clientWidth || 1;
		const isNarrow = width < 768;
		const maxItemWidth = Math.max(
			...itemMetrics.map((metric) => metric.width),
			120,
		);
		const maxItemHeight = Math.max(
			...itemMetrics.map((metric) => metric.height),
			48,
		);
		const preferredItemsPerRing = clamp(
			Math.floor((width * 1.14) / Math.max(maxItemWidth * 0.72, 120)),
			6,
			14,
		);
		const itemsPerRing = Math.min(
			cylinderObjects.length,
			preferredItemsPerRing,
		);
		const ringCount = Math.max(
			1,
			Math.ceil(cylinderObjects.length / itemsPerRing),
		);
		const circumference = itemsPerRing * (maxItemWidth + gapX);
		const radius = Math.max(circumference / (2 * Math.PI), maxItemWidth * 0.55);
		const rowGap = isNarrow ? 4 : gapY;
		const rowHeight = maxItemHeight + rowGap;
		const itemScales = cylinderObjects.map(({ item }) =>
			clamp(0.92 + getCountWeight(item) * 0.24, 0.92, 1.2),
		);
		const maxScale = Math.max(...itemScales, 1);
		const verticalHalfExtent =
			((ringCount - 1) * rowHeight) / 2 + (maxItemHeight * maxScale) / 2;
		const horizontalHalfExtent = radius + (maxItemWidth * maxScale) / 2;
		const verticalPadding = isNarrow ? 40 : 68;
		const sceneHeight = clamp(
			verticalHalfExtent * 2 + verticalPadding * 2,
			isNarrow ? 260 : 320,
			isNarrow ? 460 : 720,
		);
		const cameraFov = isNarrow ? 40 : 34;

		rotationStep = (Math.PI * 2) / itemsPerRing;
		sceneNode.style.minHeight = `${sceneHeight}px`;
		sceneNode.style.height = `${sceneHeight}px`;
		renderer.setSize(width, sceneHeight);
		camera.fov = cameraFov;
		camera.aspect = width / sceneHeight;
		const verticalFovRadians = (cameraFov * Math.PI) / 180;
		const horizontalFovRadians =
			2 * Math.atan(Math.tan(verticalFovRadians / 2) * camera.aspect);
		const distanceForHeight =
			(verticalHalfExtent + verticalPadding) / Math.tan(verticalFovRadians / 2);
		const distanceForWidth =
			(horizontalHalfExtent + 16) / Math.tan(horizontalFovRadians / 2);
		camera.position.set(
			0,
			0,
			Math.max(distanceForHeight, distanceForWidth, width * 0.7),
		);
		camera.lookAt(0, 0, 0);
		camera.updateProjectionMatrix();

		cylinderObjects.forEach(({ item, object }, index) => {
			const ringIndex = Math.floor(index / itemsPerRing);
			const indexInRing = index % itemsPerRing;
			const angleStep = (Math.PI * 2) / itemsPerRing;
			const ringOffset =
				(ringIndex % 2 === 0 ? 0 : angleStep * 0.5) +
				ringIndex * angleStep * 0.12;
			const angle = indexInRing * angleStep + ringOffset;
			const weightScale = itemScales[index] || 1;
			const y = (ringIndex - (ringCount - 1) / 2) * rowHeight;

			object.position.set(
				Math.sin(angle) * radius,
				y,
				Math.cos(angle) * radius,
			);
			object.rotation.y = angle;
			object.scale.set(weightScale, weightScale, weightScale);
		});

		renderScene();
	};

	const animate = () => {
		currentRotation += (targetRotation - currentRotation) * 0.08;
		cylinderGroup.rotation.y = currentRotation;
		renderScene();
		frameId = window.requestAnimationFrame(animate);
	};

	const rotatePrevious = () => {
		targetRotation -= rotationStep;
	};

	const rotateNext = () => {
		targetRotation += rotationStep;
	};

	updateSize();
	frameId = window.requestAnimationFrame(animate);

	const resizeObserver =
		typeof ResizeObserver === "undefined"
			? null
			: new ResizeObserver(() => {
					updateSize();
				});

	resizeObserver?.observe(sceneNode);
	previousButton?.addEventListener("click", rotatePrevious);
	nextButton?.addEventListener("click", rotateNext);

	sceneNode.addEventListener(
		"wheel",
		(event) => {
			const hasHorizontalScroll = Math.abs(event.deltaX) > 0;
			const rotationDelta = hasHorizontalScroll
				? event.deltaX
				: event.shiftKey
					? event.deltaY
					: 0;

			if (!rotationDelta) {
				return;
			}

			targetRotation += rotationDelta * sensitivity * 0.01;
			event.preventDefault();
		},
		{ passive: false },
	);

	node.addEventListener(
		"topic-cloud:destroy",
		() => {
			if (frameId) {
				window.cancelAnimationFrame(frameId);
			}

			resizeObserver?.disconnect();
			previousButton?.removeEventListener("click", rotatePrevious);
			nextButton?.removeEventListener("click", rotateNext);
		},
		{ once: true },
	);
}
